import { Button } from 'components/ui'
import React, { useEffect, useLayoutEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { AiCameraIcon } from 'components/CommonComponents/JSXIcons'
import { isRtlSelector } from 'redux/selectors/general'
import { useFormStepContext } from '../../FormStepContext'
import { AIStepType, useAILoadingContext } from '../../../AITaskCreation/AIFormStepContext'
import AIPromptStep from '../../../AITaskCreation/components/AIPromptStep'
import AISubtaskStep from '../../../AITaskCreation/components/AISubtaskStep'
import PromptGeneratorStep from '../../../AITaskCreation/components/PromptGeneratorStep'
import { PromptGeneratorInput } from '../../../AITaskCreation/components/PromptGeneratorStep/PromptGeneratorInput'
import { AI_SUBTASK_DRAFT_SLICE, useAiSubtaskDraft } from '../../../AITaskCreation/hooks/useAiSubtaskDraft'
import { SlideInPanel, useStepDirection } from '../../../UI/SlideInPanel'
import { useCreationText } from '../../hooks/useCreationText'
import { BuilderWidgetProvider } from '../../contexts/BuilderWidgetContext'
import { WidgetIsRequiredSwitch } from '../../Steps/SubtaskCreationStep/Widgets/WidgetIsRequiredSwitch'

import { SubtaskCategoryHeader } from '../SubtaskCategoryHeader'

import styles from './styles.module.css'

export interface VmAiEditorProps {
  id: string
  index: number
  active: boolean
  category?: string
  title?: string
  onClose: () => void
  onRemove: () => void
  onDone: () => void
  onBusyChange: (busy: boolean) => void
}

export function VmAiWorkflow({
  active,
  category,
  title,
  id,
  index,
  onClose,
  onRemove,
  onBusyChange,
  complete,
  onDraftChange,
}: VmAiEditorProps & { complete: (data: any) => void; onDraftChange: (data: any) => void }) {
  const { stepStack, setStepStack, goToPreviousStep, sumbitStep, isMediaUploading, setSubtaskAiGenerating } =
    useFormStepContext()
  const { isGenerating } = useAILoadingContext()
  const { savePrompt, startGeneration } = useAiSubtaskDraft()
  const rtl = useSelector(isRtlSelector)
  const t = useCreationText()

  const step = stepStack[stepStack.length - 1]
  const helper = step === AIStepType.AI_PROMPT_GENERATOR

  const workflow = useRef<HTMLDivElement>(null)
  const footer = useRef<HTMLElement>(null)

  const preview = step === AIStepType.AI_SUBTASK
  const busy = isGenerating || isMediaUploading

  const { direction } = useStepDirection(stepStack.length)

  useLayoutEffect(() => {
    if (!active || !helper || !footer.current) return

    const element = workflow.current

    const measure = () =>
      element?.style.setProperty('--prompt-generator-composer-height', `${footer.current?.offsetHeight || 0}px`)

    measure()

    const observer = typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(measure)

    observer?.observe(footer.current)
    return () => {
      observer?.disconnect()
      element?.style.removeProperty('--prompt-generator-composer-height')
    }
  }, [active, helper])

  useEffect(() => {
    if (active) {
      onBusyChange(isGenerating)
      setSubtaskAiGenerating?.(isGenerating)
    }
  }, [active, isGenerating, onBusyChange, setSubtaskAiGenerating])

  return (
    <div ref={workflow} className={styles.workflow} data-workflow-id={id} hidden={!active}>
      <SubtaskCategoryHeader category={category} title={title}>
        <Button
          variant="icon"
          height="auto"
          type="button"
          className={stepStack.length > 1 ? styles.back : 'creation-editor-back'}
          isDisabled={busy}
          aria-label={stepStack.length > 1 ? t.aiBack : t.back}
          onClick={stepStack.length > 1 ? goToPreviousStep : onClose}
        >
          {rtl ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
        </Button>
        <AiCameraIcon width="40px" height="40px" iconWidth={22} />
        <div className="creation-editor-heading">
          <strong>{t.ai}</strong>
          <small>{helper ? t.aiHelper : preview ? t.aiPreviewStep : t.aiPromptStep}</small>
        </div>
        <BuilderWidgetProvider>
          <WidgetIsRequiredSwitch subtaskPath={`sub_tasks[${index}]`} compact />
        </BuilderWidgetProvider>
      </SubtaskCategoryHeader>
      <div className={`creation-editor-scroll ${styles.content} ${helper ? styles.conversation : ''}`}>
        {active && (
          <SlideInPanel key={step} direction={direction} grow>
            {helper ? (
              <PromptGeneratorStep />
            ) : preview ? (
              <AISubtaskStep
                draftKey={AI_SUBTASK_DRAFT_SLICE}
                showSummary={false}
                showTaskTitle={false}
                onDone={complete}
                onDraftChange={onDraftChange}
              />
            ) : (
              <AIPromptStep
                onSaveDraft={savePrompt}
                onNext={(data) => {
                  savePrompt(data)
                  startGeneration(data, `vm-subtask-${id}`)
                  setStepStack((previous) => [...previous, AIStepType.AI_SUBTASK])
                }}
              />
            )}
          </SlideInPanel>
        )}
      </div>
      <footer ref={footer} className={`creation-editor-footer ${styles.footer} ${helper ? styles.conversation : ''}`}>
        {active &&
          (helper ? (
            <PromptGeneratorInput />
          ) : (
            <>
              <Button
                px="14px"
                py="9px"
                bg="#fdf5f5"
                color="#bf4944"
                borderWidth="1px"
                borderColor="#eccfcd"
                _hover={{ bg: '#fdebea', borderColor: '#e5bdb9' }}
                variant="secondary"
                height="auto"
                type="button"
                className="creation-delete"
                isDisabled={busy}
                onClick={onRemove}
              >
                {t.remove}
              </Button>
              <small role={isGenerating ? 'status' : undefined}>{isGenerating ? t.aiGenerating : t.aiStepHint}</small>
              <Button
                px="24px"
                py={preview ? '9px' : '11px'}
                bg={preview ? 'white' : '#222222'}
                color={preview ? '#22272a' : 'white'}
                borderWidth={preview ? '1px' : 0}
                borderColor="#343a3e"
                _hover={{ bg: preview ? '#f8fafc' : '#333333' }}
                variant={preview ? 'secondary' : 'primary'}
                height="auto"
                type="button"
                className={preview ? 'creation-done' : `creation-next ${styles.next}`}
                isDisabled={busy}
                onClick={sumbitStep}
              >
                {preview ? t.done : t.aiNext}
              </Button>
            </>
          ))}
      </footer>
    </div>
  )
}
